import React, { useEffect, useState } from "react";
import { isDirty, getFormValues, destroy, initialize, getFormInitialValues } from "redux-form";

import { isEqual } from "lodash";

import {
	Modal,
	Button,
	ProgressIndicator
} from "rhombus-react";
import { useDispatch, useSelector } from "react-redux";

import { VersionDetails, updateVersionVariables } from "reducers/VersionDetails";
import { fetchSingleVersion, loadCreativeVersions } from "reducers/CreativeVersions";
import { clearCropMetadata } from "reducers/Refinements";

import EditVersion from "./inlineedit/EditVersion";

import Toolbar from "./social/Toolbar";


import SaveAsModal from "./SaveAsModal";

import useSearchCache from "hooks/useSearchCache";
import useConfirmClose from "hooks/useConfirmClose";

import {
	// isAfterEffectsCreative,
	isMaxDynamicSocialVersions,
	useModalStack
} from "utils/common";

import { ASSET_TYPES } from "utils/constants";
import { getVariables, addLibraryIdToVideos } from "utils/VersionHelpers";
import { EDIT_VERSION_BLADE_FORM } from "forms/list";

import DisplayPreview from "widgets/modals/previews/DisplayPreview";

import "./RefinedVersionEdit.css";

function pristineSelector(state){
	/*
		This function is used to check if the form is pristine, to see if we need to confirm before closing.
		We have null change events that happen in TextEditor for some reason, which prevent us from
		getting accurate metadata from the `isDirty`/`isPristine` redux-form selectors.

		There are also values added like `ctaFormatted` for `cta` fields by TextEditor, so we can't
		just do a full comparison between `values` and `initialValues`.

		Instead, we go key by key through `initialValues` and check that the corresponding value
		in `values` matches it.
	*/

	const values = getFormValues(EDIT_VERSION_BLADE_FORM)(state) || {};
	const initialValues = getFormInitialValues(EDIT_VERSION_BLADE_FORM)(state) || {};

	return Object.keys(initialValues).every(key => isEqual(values[key], initialValues[key]));
}

export function mapAssetToOption(asset){
	/*
		Text assets also appear with their ({id}) in the dropdown.
	*/

	let label = asset.name;
	if (asset.assetType === ASSET_TYPES.TEXT) {
		label = `${label} (${asset.id})`;
	}

	return {
		label,
		data: asset.name,
		assetId: asset.id,
		filesize: asset.filesize,
		type: asset.assetType,
	};
}

export function getAssetOptionsFromPayload(response){
	return response?.payload?.data?.items?.map(mapAssetToOption) || [];
}

export function initializeEditVersionValues(variables = {}) {
	return variables.reduce((obj, variable) => {
		let { name } = variable;
		return  {
			...obj,
			[name]: variable,
		};
	}, {});
}

const {
	loadVersionDetails,
	clearVersionDetails,
} = VersionDetails;

export default function RefinedVersionEdit({
	onClose,
	libraryId,
	creativeId,
	versionId,
	...props
}){
	let dispatch = useDispatch()
		, hasEdits = useSelector(isDirty(EDIT_VERSION_BLADE_FORM))
		, formValues = useSelector(getFormValues(EDIT_VERSION_BLADE_FORM))
		, [isFullScreen, setIsFullScreen] = useState(false)
		, [isSaving, setIsSaving] = useState(false)
		, [isSavingAs, setIsSavingAs] = useState(false)
		, [versionInfo, setVersionInfo] = useState()
		, {
			detailsLoaded,
			items,
			versionVariables,
			creativeDetails,
			totalItems,
		} = useSelector((state)=>{
			return {
				detailsLoaded:state.versionDetails.isLoaded,
				items:state.creativeVersions.items,
				totalItems: state.creativeVersions.totalItems,
				versionVariables:state.versionDetails.items,
				creativeDetails:state.creativeDetails,
			};
		})
		, selection = items?.find(ea => ea.id === parseInt(versionId))
		, title = versionInfo?.name || "Loading"
		;

	useEffect(()=>{
		dispatch(loadVersionDetails(libraryId, parseInt(creativeId), parseInt(versionId)));
		if(!selection){
			dispatch(fetchSingleVersion(libraryId, parseInt(creativeId), parseInt(versionId)))
				.then(resp=>setVersionInfo(resp));
		}
		setVersionInfo(selection);
	}, [libraryId, creativeId, versionId, selection]);

	useEffect(() => {
		if (!versionVariables) { return; }

		const initValues = initializeEditVersionValues(versionVariables);

		dispatch(initialize(EDIT_VERSION_BLADE_FORM, initValues));
	}, [versionVariables]);

	const { search: searchAssets } = useSearchCache({
		key: "assets",
		urlParams: { libraryId },
		transform: getAssetOptionsFromPayload,
	});

	function closeModal(){
		dispatch(clearVersionDetails());
		dispatch(destroy(EDIT_VERSION_BLADE_FORM));
		// Clear crop metadata map for all variables
		dispatch(clearCropMetadata());
		onClose();
	}

	const confirmedClose = useConfirmClose(
		EDIT_VERSION_BLADE_FORM,
		closeModal,
		"Are you sure you want to cancel version editing? All data entered will be lost.",
		pristineSelector
	);

	function onToggleFullScreen(){
		setIsFullScreen(!isFullScreen);
	}

	const commonModalProps = {
		type: "blade",
		setAppTitle:false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,
	};

	const [, { /*pushModal*/ }, ModalStackHead] = useModalStack({ commonModalProps });

	function whoFramedJCRM(what){
		setIsSaving(true);
		let updatedValues = getVariables(what, libraryId);

		dispatch(updateVersionVariables(libraryId, creativeId, versionId, updatedValues))
			.then((response) => {
				if(response?.success){
					setIsSaving(false);
					closeModal();

					/*
						The update variables endpoint only returns the new variables, not the whole version.

						The version's modified date and size can potentially change, so we have to refetch
						to get the updated row data and respect the sorting and filtering.
					*/
					dispatch(loadCreativeVersions(libraryId, creativeId));
				}
			});
	}

	function saveAsVersion() {
		setIsSavingAs(true);
	}

	const $saveAsModal = isSavingAs && <SaveAsModal {...{
		libraryId,
		creativeId,
		version: {
			...versionInfo,
			variableConfig: addLibraryIdToVideos(formValues, libraryId),
		},
		onClose(){ setIsSavingAs(false); },
	}} />;

	function Footer(){
		let shouldDisableAllButtons = !hasEdits || isSaving;
		// const hidePreviewButton = isAfterEffectsCreative(creativeDetails);

		return (
			<>
				<Button theme="default" onClick={confirmedClose}>Cancel</Button>
				<div className="footer-actions">
					<Button
						disabled={shouldDisableAllButtons || isMaxDynamicSocialVersions(totalItems, creativeDetails)}
						onClick={saveAsVersion}>Save As...</Button>
					<Button
						disabled={shouldDisableAllButtons}
						theme="green"
						type="submit"
						form={EDIT_VERSION_BLADE_FORM}>Save Version</Button>
				</div>
			</>
		);
	}	//end Footer()

	function Content(){
		return (
			<div className="main-content-container refined-version-content">
				<ProgressIndicator isLoading={!detailsLoaded || isSaving} />
				<Toolbar />
				<div className="main-content">
					<DisplayPreview
						creative={{
							...creativeDetails,
							id:creativeId,
							versionInfo:formValues,
						}}
						libraryId={libraryId}
						creativeId={creativeId}
					/>
					<EditVersion
						searchAssets = {searchAssets}
						libraryId = {libraryId}
						onSave={whoFramedJCRM}
						variables={versionVariables}
					/>
				</div>
			</div>
		);
	}

	let modalProps = {
		...commonModalProps,
		title:`Edit Version - ${title}`,
		className: "edit-version-blade refined-version-edit",

		renderFooter: Footer,
		renderContent: Content,

		onClose: confirmedClose,
	};

	const blade = ModalStackHead ? <ModalStackHead versionInfo={versionInfo} creativeId={creativeId} /> : <Modal {...modalProps} />;

	return (
		<>
			{$saveAsModal}
			{blade}
		</>
	);
}

RefinedVersionEdit.displayName = "RefinedVersionEdit";
